import { DeliveryDining } from '@mui/icons-material';
import { Box, Typography, Theme } from '@mui/material';
import { useCallback } from 'react';
import { changeOrderStatus } from '../../../api/order/changeOrderStatus';
import { Order as OrderType, OrderStatus } from '../../../apiTypes';
import PrimaryButton from '../../../components/PrimaryButton';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import EditDeliveryOrderButton from './EditDeliveryOrderButton';

type Props = {
  order: DbDocument<OrderType>;
};

const OrderDeliveryBlock = ({ order }: Props) => {
  const { lang } = useLocale();
  const { worker } = useCafe();
  const { cafeSlug, cafe } = useAdmin();

  const handleChangeOrderStatus = useCallback(
    async (newStatus: OrderStatus) => {
      if (worker) {
        await changeOrderStatus({ order, worker, cafeSlug, newStatus });
      }
    },
    [cafeSlug, order, worker]
  );

  const bgStyle = useCallback(
    (t: Theme) => {
      if (order.status === 'ordered') {
        return `${t.palette.primary.main}11`;
      }

      if (order.status === 'processing') {
        return `${t.palette.secondary.main}11`;
      }

      return `${t.palette.success.main}11`;
    },
    [order]
  );
  return (
    <Box
      sx={{
        borderRadius: 1,
        bgcolor: bgStyle,
        m: 1,
        p: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeliveryDining sx={{ color: (t) => t.palette.text.secondary, ml: 1 }} />
        {order.status && (
          <Typography color={(t) => t.palette.text.secondary}>
            {order.status === 'complete' ? t.delivered[lang] : t[`status_${order.status}`][lang]}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <EditDeliveryOrderButton order={order} />

        {order.status === 'ordered' && (
          <PrimaryButton variant="contained" onClick={() => handleChangeOrderStatus('processing')}>
            {t.confirm[lang]}
          </PrimaryButton>
        )}

        {order.status === 'processing' && (
          <PrimaryButton variant="contained" color="secondary" onClick={() => handleChangeOrderStatus('delivering')}>
            {t.status_delivering[lang]}
          </PrimaryButton>
        )}

        {order.status === 'delivering' && (
          <PrimaryButton variant="contained" color="secondary" onClick={() => handleChangeOrderStatus('complete')}>
            {t.delivered[lang]}
          </PrimaryButton>
        )}
      </Box>
    </Box>
  );
};

export default OrderDeliveryBlock;
